import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, AlertTriangle, TrendingUp, Gauge, Target, ChevronDown } from 'lucide-react';
import { ServiceItem } from '../../types';
import { NEP_BADGE, NEP_HINT } from '../../services/nepPosition';
import { useRememberedState } from '../../hooks/useRemembered';
import { SectionHeading } from './ModuleTile';
import { LeistungsVerlaufChart, VerlaufModus } from './LeistungsVerlaufChart';
import { StandRinge } from './StandRinge';
import {
    Laufzeit, LeistungsSumme, LeistungsZeile, PhaseArt, PHASE_NAME, StandAnsicht,
    anteil, datumAnzeige, gesamtBalkenFarbe, isoTag, leistungsSumme, leistungsSummeJePhase, leistungsZeilen, mengeAnzeige,
    phasenLinie, positionsPrognose, standFuerAnsicht, standPrognose, standSatz,
} from './projektStand';

/**
 * PROJEKTSTAND — die erste Karte im Projekt-Hub (docs/projektstand-hub-plan.md).
 *
 * Betreiber 25.09.2026, in Runden: optisch sehen, wie weit ein Projekt ist —
 * nur Laufzeit und Leistungen, Planung und Ausfuehrung getrennt; dezent, feste
 * Groesse, animiert, interaktiv; kein Scrollbalken; „mehr herausholen",
 * „ein richtiger Wow-Effekt".
 *
 *  · BLICKFANG: der Doppelring (StandRinge) — aussen Zeit, innen Leistung.
 *  · AUSSAGE: ein Satz Klartext (standSatz) und drei Kennzahlen — Abstand zum
 *    Plan, Tempo gegen noetiges Tempo, Prognose zum Ende.
 *  · KURVE: Verlauf mit Prognose oder Leistung je Monat (Umschalter).
 *  · POSITIONEN: alle als Kacheln, nie gescrollt; die Phase blendet die
 *    fremden Kacheln nur ab — die Karte behaelt beim Bedienen ihre Groesse.
 *  · BEWEGUNG: Ringe und Zahlen laufen auf, die Kurve zeichnet sich, Kacheln
 *    blenden gestaffelt ein, die Reiter-Markierung gleitet. „Weniger
 *    Bewegung" im System schaltet alles ab.
 *
 * Reine Anzeige; gerechnet wird in projektStand.ts.
 */

interface Props {
    laufzeit: Laufzeit;
    /** LV (beim Tochterprojekt das der Mutter) — wie „Leistung & Budget". */
    services: ServiceItem[];
    /** Buchungsprotokoll zur Kurve. */
    log: ReadonlyArray<{ date?: string; serviceId?: string; quantity?: number; bookingReference?: string }>;
    heute: string;
    canSeeFinancials: boolean;
    /** Name des Mutterprojekts, wenn die Leistungen von dort kommen. */
    mutterName?: string;
    isReadOnly: boolean;
    /** Schluessel fuer das Gedaechtnis der Ansicht je Projekt (wie im Hub). */
    memoryKey: string;
    onTrackService: (service: ServiceItem) => void;
}

const ANSICHTEN: StandAnsicht[] = ['gesamt', 'planung', 'ausfuehrung'];

/** Phasenpunkte — woertlich fuer Tailwind. */
const PHASE_PUNKT: Record<PhaseArt, string> = { planung: 'bg-blue-300', ausfuehrung: 'bg-blue-500' };

const euro = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
const zahl = (n: number, stellen = 0) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen }).format(n);

/** Farbe der Leistung: dieselbe Regel wie der Balken in „Leistung & Budget". */
const leistungsFarbe = (p: number) => p > 100 ? 'var(--color-red-500, #dd2b2b)'
    : p > 85 ? 'var(--color-orange-400, #fb923c)'
    : 'var(--color-emerald-500, #0d9f72)';

const bewegungReduziert = (): boolean =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Erst im zweiten Frame true — so laeuft beim Oeffnen alles von 0 an. */
const useEinblenden = (): boolean => {
    const [bereit, setBereit] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setBereit(true));
        return () => cancelAnimationFrame(id);
    }, []);
    return bereit || bewegungReduziert();
};

/** Zaehlt weich zum Zielwert — beim Oeffnen von 0, beim Wechsel vom alten Wert aus. */
const useZaehler = (ziel: number, dauer = 900): number => {
    const [wert, setWert] = useState(0);
    const vorher = useRef(0);
    useEffect(() => {
        if (bewegungReduziert()) return;
        const start = vorher.current;
        const t0 = performance.now();
        let id = 0;
        const schritt = (t: number) => {
            const f = Math.min(1, (t - t0) / dauer);
            const v = start + (ziel - start) * (1 - Math.pow(1 - f, 3));
            vorher.current = v;
            setWert(v);
            if (f < 1) id = requestAnimationFrame(schritt);
        };
        id = requestAnimationFrame(schritt);
        return () => cancelAnimationFrame(id);
    }, [ziel, dauer]);
    return bewegungReduziert() ? ziel : wert;
};

const PhasenPunkt: React.FC<{ art: PhaseArt }> = ({ art }) => (
    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PHASE_PUNKT[art]}`} aria-hidden="true" />
);

/**
 * Umschalter mit gleitender Markierung. Gleich breite Felder — die
 * Markierung braucht so keine Messung, nur ihren Index.
 */
function Umschalter<T extends string>({ werte, aktiv, onWahl, beschriftung, label, className = '' }: {
    werte: T[]; aktiv: T; onWahl: (w: T) => void; beschriftung: (w: T) => React.ReactNode; label: string; className?: string;
}) {
    const index = Math.max(0, werte.indexOf(aktiv));
    return (
        <div role="tablist" aria-label={label} className={`relative grid bg-slate-100 rounded-lg p-0.5 ${className}`} style={{ gridTemplateColumns: `repeat(${werte.length}, minmax(0, 1fr))` }}>
            <span
                aria-hidden="true"
                className="absolute top-0.5 bottom-0.5 rounded-md bg-white shadow-sm transition-[left] duration-300 ease-out motion-reduce:transition-none"
                style={{ width: `calc((100% - 4px) / ${werte.length})`, left: `calc(2px + ${index} * (100% - 4px) / ${werte.length})` }}
            />
            {werte.map(w => (
                <button
                    key={w}
                    type="button"
                    role="tab"
                    aria-selected={w === aktiv}
                    onClick={() => onWahl(w)}
                    className={`relative z-10 px-2 py-1 text-[11px] font-bold flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors ${w === aktiv ? 'text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    {beschriftung(w)}
                </button>
            ))}
        </div>
    );
}

/** Eine der drei Kennzahlen unter den Ringen. */
const Kennzahl: React.FC<{ icon: React.ReactNode; label: string; wert: string; unter: string; warnung?: boolean; titel?: string }> = ({ icon, label, wert, unter, warnung, titel }) => (
    <div className="rounded-xl bg-slate-50 px-3 py-2 min-w-0" title={titel}>
        <div className="text-[11px] text-slate-400 flex items-center gap-1">{icon}{label}</div>
        <div className={`text-sm font-bold leading-tight mt-0.5 ${warnung ? 'text-amber-700' : 'text-slate-800'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{wert}</div>
        <div className="text-[11px] text-slate-500 truncate">{unter}</div>
    </div>
);

/**
 * Eine Position als EINE schlanke Zeile: Nummer, Titel, Mini-Balken, Menge,
 * ein Zeichen (✓ erbracht, ⚠ knapp / ueber dem Soll). Was nicht in die Zeile
 * passt — Hochrechnung, Betrag, Einheit — steht im Tooltip. Betreiber
 * 25.09.2026: die Kacheln machten die Karte „viel zu gross".
 */
const PositionsZeile: React.FC<{
    z: LeistungsZeile; index: number; sichtbar: boolean; gedimmt: boolean; linie: number | null;
    prognose: { text: string; knapp: boolean } | null; mitPhase: boolean;
    canSeeFinancials: boolean; isReadOnly: boolean; onClick: () => void;
}> = ({ z, index, sichtbar, gedimmt, linie, prognose, mitPhase, canSeeFinancials, isReadOnly, onClick }) => {
    const s = z.service;
    const preis = s.unitPrice || 0;
    const zustandText = z.zustand === 'erledigt' ? 'erbracht'
        : z.zustand === 'ueber' ? `${Math.round(z.prozent ?? 0)} % — über dem Soll`
        : z.zustand === 'offen' ? 'noch offen'
        : z.zustand === 'nep' ? `Eventualposition — ${NEP_HINT}`
        : z.zustand === 'ohne_soll' ? 'ohne Sollmenge'
        : prognose?.text ?? `${Math.round(z.prozent ?? 0)} % erbracht`;
    const titel = [
        `${s.lvPosition} ${s.title}`,
        `${mengeAnzeige(z.used)}${z.prozent !== null ? ` / ${mengeAnzeige(z.contracted)}` : ''} ${s.unit} — ${zustandText}`,
        canSeeFinancials && preis > 0 ? (z.contracted > 0 ? `${euro(z.used * preis)} von ${euro(z.contracted * preis)}` : `${euro(z.used * preis)} gebucht`) : '',
        isReadOnly ? '' : 'Klick: Leistung buchen',
    ].filter(Boolean).join('\n');
    const mitBalken = z.prozent !== null;
    const warnung = z.zustand === 'ueber' ? 'text-red-600' : prognose?.knapp ? 'text-amber-600' : null;

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isReadOnly}
            title={titel}
            className={`w-[calc(100%+0.5rem)] -mx-1 px-1 py-[3px] rounded-md break-inside-avoid grid grid-cols-[minmax(0,1fr)_48px_64px] items-center gap-2 text-left hover:bg-slate-50 disabled:hover:bg-transparent disabled:cursor-default motion-reduce:!transition-none ${gedimmt ? 'opacity-35' : ''}`}
            style={{
                opacity: sichtbar ? undefined : 0,
                transition: `opacity 400ms ease-out ${index * 20}ms, background-color 150ms`,
            }}
        >
            <span className="min-w-0 flex items-center gap-1.5">
                {mitPhase && <PhasenPunkt art={z.phase} />}
                <span className="font-mono text-[11px] text-slate-400 shrink-0">{s.lvPosition}</span>
                <span className="text-xs text-slate-600 truncate">{s.title}</span>
                {z.zustand === 'nep' && <span className="text-[10px] font-black text-amber-800 bg-amber-100 border border-amber-200 px-1 rounded shrink-0">{NEP_BADGE}</span>}
            </span>
            <span className="relative h-1 bg-slate-100 rounded-full">
                {mitBalken && (
                    <>
                        <span className="absolute inset-0 rounded-full overflow-hidden">
                            <span
                                className={`block h-full rounded-full ${z.zustand === 'ueber' ? 'bg-red-500' : z.zustand === 'erledigt' ? 'bg-emerald-500' : gesamtBalkenFarbe(z.prozent ?? 0)} transition-[width] duration-700 ease-out motion-reduce:transition-none`}
                                style={{ width: sichtbar ? `${Math.min(100, z.prozent ?? 0)}%` : 0, transitionDelay: `${100 + index * 20}ms` }}
                            />
                        </span>
                        {/* Pauschalen (1/1) sind erledigt oder nicht — die Zeit sagt dort nichts. */}
                        {z.contracted > 1 && linie !== null && (
                            <span className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-slate-600 rounded-full pointer-events-none" style={{ left: `calc(${Math.min(100, Math.max(0, linie))}% - 1px)` }} />
                        )}
                    </>
                )}
            </span>
            <span className="text-[11px] text-slate-500 whitespace-nowrap flex items-center justify-end gap-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {mitBalken ? `${mengeAnzeige(z.used)}/${mengeAnzeige(z.contracted)}` : mengeAnzeige(z.used)}
                {warnung ? <AlertTriangle className={`w-3 h-3 shrink-0 ${warnung}`} aria-label={zustandText} />
                    : z.zustand === 'erledigt' ? <Check className="w-3 h-3 shrink-0 text-emerald-600" aria-label="erbracht" />
                    : <span className="w-3 shrink-0" />}
            </span>
        </button>
    );
};

/** Die Liste — eigene Komponente, damit sie beim Aufklappen neu einblendet. */
const PositionsListe: React.FC<{ kinder: (sichtbar: boolean) => React.ReactNode }> = ({ kinder }) => {
    const sichtbar = useEinblenden();
    // Spaltensatz statt Raster: die Liste laeuft spaltenweise in LV-Reihenfolge
    // (erst die Planung oben links), nicht zeilenweise quer ueber die Spalten.
    return <div className="mt-2 columns-1 md:columns-2 xl:columns-3 gap-x-6">{kinder(sichtbar)}</div>;
};

export const ProjectStandKarte: React.FC<Props> = ({ laufzeit, services, log, heute, canSeeFinancials, mutterName, isReadOnly, memoryKey, onTrackService }) => {
    const [gewaehlt, setAnsicht] = useState<StandAnsicht>('gesamt');
    const [modus, setModus] = useState<VerlaufModus>('verlauf');
    const [positionenOffen, setPositionenOffen] = useRememberedState(`${memoryKey}.stand.positionen`, false);

    const jePhase = useMemo(() => leistungsSummeJePhase(services), [services]);
    const gesamtSumme = useMemo(() => leistungsSumme(services), [services]);
    const mitPhasen = laufzeit.abschnitte.length > 0 || (jePhase.planung.anzahl > 0 && jePhase.ausfuehrung.anzahl > 0);
    // Verschwindet der Umschalter (letzte Planungsposition entfernt, keine
    // Phasendaten), darf die Karte nicht auf der unsichtbaren Phase haengen.
    const ansicht: StandAnsicht = mitPhasen ? gewaehlt : 'gesamt';

    const eingabe = useMemo(() => ({ services, log, laufzeit, heute, inEuro: canSeeFinancials }), [services, log, laufzeit, heute, canSeeFinancials]);
    const daten = useMemo(() => standFuerAnsicht(eingabe, ansicht), [eingabe, ansicht]);
    const prognose = useMemo(() => standPrognose(daten.verlauf, heute), [daten.verlauf, heute]);

    // Alle Positionen, Planung vor Ausfuehrung — die Kacheln stehen immer
    // alle da; die Ansicht blendet nur ab.
    const alleZeilen = useMemo(() => {
        const z = leistungsZeilen(services);
        return mitPhasen ? [...z.filter(x => x.phase === 'planung'), ...z.filter(x => x.phase === 'ausfuehrung')] : z;
    }, [services, mitPhasen]);

    const wert = (s: LeistungsSumme) => (canSeeFinancials
        ? { ist: s.totalUsed, soll: s.totalContracted }
        : { ist: s.totalQtyUsed, soll: s.totalQtyContracted });
    const leistung = wert(daten.summe);
    const leistungProzent = leistung.soll > 0 ? anteil(leistung.ist, leistung.soll) : null;

    const zeitAnim = useZaehler(daten.zeitProzent ?? 0);
    const leistungAnim = useZaehler(leistungProzent ?? 0);
    const abweichungAnim = useZaehler(prognose.abweichung ?? 0);
    const prognoseAnim = useZaehler(prognose.prognoseEnde ?? 0);

    const reiterProzent = (a: StandAnsicht): number | null => {
        const w = wert(a === 'gesamt' ? gesamtSumme : jePhase[a]);
        return w.soll > 0 ? anteil(w.ist, w.soll) : null;
    };

    const phaseDer = (p: PhaseArt) => laufzeit.abschnitte.find(a => a.art === p);
    const linieFuer = (p: PhaseArt): number | null =>
        laufzeit.abschnitte.length === 0 ? daten.linie : ansicht === 'gesamt' || ansicht === p ? phasenLinie(phaseDer(p), laufzeit) : null;
    const endeFuer = (p: PhaseArt): string | null => isoTag(phaseDer(p)?.bisTag ?? laufzeit.ende);
    // Die Hochrechnung je Position — auch bei zugeklappter Liste, denn die
    // Kopfzeile nennt die Zahl der knappen Positionen.
    const prognosen = new Map(alleZeilen.map(z => [z.service.id, positionsPrognose(z, log, heute, endeFuer(z.phase))]));
    // Dieselbe Bezugsgruppe wie „x von y erbracht": die gewaehlte Ansicht.
    const knapp = daten.zeilen.filter(z => z.zustand === 'ueber' || prognosen.get(z.service.id)?.knapp).length;

    const erbracht = daten.zeilen.filter(z => z.zustand === 'erledigt' || z.zustand === 'ueber').length;
    const mitSoll = daten.zeilen.filter(z => z.prozent !== null).length;
    const zeitZustand = laufzeit.zustand === 'ruht' ? 'ruht' : daten.zeitWarnung ? 'warnung' : 'normal';
    const zeitName = ansicht === 'gesamt' ? 'Zeit' : `${PHASE_NAME[ansicht]}szeit`;

    // Kennzahlen
    const a = prognose.abweichung;
    const abwText = a === null ? '–' : Math.abs(a) < 2.5 ? 'im Plan' : `${a > 0 ? '+' : '−'}${zahl(Math.abs(abweichungAnim))} Pkt.`;
    const abwUnter = a === null ? 'kein Zeitraum' : Math.abs(a) < 2.5 ? 'Leistung = Zeit' : a > 0 ? 'vor der Zeit' : 'hinter der Zeit';
    const tempo = prognose.tempoProMonat;
    const noetig = prognose.noetigProMonat;
    const tempoWarnung = tempo !== null && noetig !== null && tempo < noetig * 0.9;
    const tempoEuro = tempo !== null && canSeeFinancials ? ` ≈ ${euro((tempo / 100) * leistung.soll)} pro Monat` : '';
    const progWarnung = prognose.prognoseEnde !== null && (prognose.prognoseEnde < 90 || prognose.prognoseEnde > 105);

    return (
        <section data-tutorial="hub.projektstand">
            <SectionHeading label="Projektstand" hint={`Stand ${datumAnzeige(heute)}`} />
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 md:p-5">
                {/* Kopfzeile — feste Hoehe, auch ohne Umschalter. */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4 min-h-[30px]">
                    {mitPhasen ? (
                        <Umschalter<StandAnsicht>
                            label="Ansicht"
                            werte={ANSICHTEN}
                            aktiv={ansicht}
                            onWahl={setAnsicht}
                            className="w-full sm:w-[360px]"
                            beschriftung={w => {
                                const p = reiterProzent(w);
                                return (
                                    <>
                                        {w !== 'gesamt' && <span className="hidden sm:inline-flex"><PhasenPunkt art={w} /></span>}
                                        {w === 'gesamt' ? 'Gesamt' : PHASE_NAME[w]}
                                        {p !== null && <span className="font-semibold text-slate-400">{Math.round(p)} %</span>}
                                    </>
                                );
                            }}
                        />
                    ) : (
                        <span className="text-[11px] text-slate-400">Laufzeit und Leistungen</span>
                    )}
                    <div className="flex items-center gap-3 ml-auto">
                        {laufzeit.aktuellePhase && (
                            <span className="text-[11px] text-slate-400 whitespace-nowrap hidden md:inline">jetzt: <span className="font-bold text-slate-600">{PHASE_NAME[laufzeit.aktuellePhase]}</span></span>
                        )}
                        {daten.verlauf && (
                            <Umschalter<VerlaufModus>
                                label="Darstellung"
                                werte={['verlauf', 'monate'] as VerlaufModus[]}
                                aktiv={modus}
                                onWahl={setModus}
                                className="w-[150px]"
                                beschriftung={w => (w === 'verlauf' ? 'Verlauf' : 'Monate')}
                            />
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-5 lg:h-[300px]">
                    {/* Links: Ringe, Satz, Kennzahlen */}
                    <div className="lg:col-span-5 flex flex-col gap-3 min-w-0">
                        <div className="flex items-center gap-5">
                            <StandRinge
                                zeit={daten.zeitProzent === null ? null : zeitAnim}
                                leistung={leistungProzent === null ? null : leistungAnim}
                                leistungFarbe={leistungsFarbe(leistungProzent ?? 0)}
                                abschnitte={ansicht === 'gesamt' ? laufzeit.abschnitte : []}
                                zeitZustand={zeitZustand}
                                phase={ansicht === 'gesamt' ? undefined : ansicht}
                                onPhase={setAnsicht}
                            >
                                <span className="text-2xl font-bold text-slate-800 leading-none">
                                    {leistungProzent === null ? '–' : `${Math.round(leistungAnim)} %`}
                                </span>
                                <span className="text-[11px] text-slate-400 mt-1">Leistung</span>
                            </StandRinge>
                            <div className="min-w-0 flex-1 space-y-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full shrink-0 ${zeitZustand === 'warnung' ? 'bg-red-500' : zeitZustand === 'ruht' ? 'bg-slate-400' : ansicht === 'planung' ? 'bg-blue-300' : 'bg-blue-500'}`} />
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{zeitName}</span>
                                        <span className="ml-auto text-sm font-bold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>{daten.zeitProzent === null ? '–' : `${Math.round(zeitAnim)} %`}</span>
                                    </div>
                                    <div className={`text-[11px] mt-0.5 pl-4 truncate ${daten.zeitWarnung ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{daten.zeitText}</div>
                                    <div className="text-[11px] text-slate-400 pl-4 truncate">
                                        {daten.zeitVon && daten.zeitBis ? `${datumAnzeige(daten.zeitVon)} – ${datumAnzeige(daten.zeitBis)}` : 'Start und Ende unter „Zeitplan & Intervall“'}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: leistungsFarbe(leistungProzent ?? 0) }} />
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{mutterName ? 'Leistung · Mutter' : 'Leistung'}</span>
                                        <span className="ml-auto text-sm font-bold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>{leistungProzent === null ? '–' : `${Math.round(leistungAnim)} %`}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5 pl-4 truncate">
                                        {canSeeFinancials ? `${euro(leistung.ist)} von ${euro(leistung.soll)}` : `${mengeAnzeige(leistung.ist)} von ${mengeAnzeige(leistung.soll)} Einheiten`}
                                    </div>
                                    <div className="text-[11px] text-slate-400 pl-4 truncate">{erbracht} von {mitSoll} Positionen erbracht</div>
                                </div>
                            </div>
                        </div>

                        {/* Der Satz: feste Hoehe, damit die Karte beim Umschalten nicht springt. */}
                        <p className="text-xs text-slate-600 leading-relaxed h-[58px] lg:h-[40px] line-clamp-3 lg:line-clamp-2" title={standSatz(prognose)}>
                            {standSatz(prognose)}
                        </p>

                        <div className="grid grid-cols-3 gap-2 lg:mt-auto">
                            <Kennzahl icon={<Target className="w-3 h-3" />} label="Zum Plan" wert={abwText} unter={abwUnter} warnung={a !== null && a <= -10} />
                            <Kennzahl
                                icon={<Gauge className="w-3 h-3" />}
                                label="Tempo/Monat"
                                wert={tempo === null ? '–' : `${zahl(tempo, 1)} %`}
                                unter={noetig !== null ? `nötig ${zahl(noetig, 1)} %` : tempo === null ? 'zu wenig Buchungen' : 'Ø 90 Tage'}
                                warnung={tempoWarnung}
                                titel={tempo !== null ? `Ø der letzten 90 Tage${tempoEuro}` : undefined}
                            />
                            <Kennzahl
                                icon={<TrendingUp className="w-3 h-3" />}
                                label="Prognose"
                                wert={prognose.prognoseEnde === null ? '–' : `${Math.round(prognoseAnim)} %`}
                                unter={prognose.prognoseTag ? `zum ${datumAnzeige(prognose.prognoseTag).slice(0, 6)}`
                                    : daten.zeitBis && (isoTag(daten.zeitBis) ?? '') < heute ? 'Zeitraum abgelaufen'
                                    : 'kein Ende gepflegt'}
                                titel={prognose.prognoseTag ? `Stand am ${datumAnzeige(prognose.prognoseTag)} bei gleichem Tempo` : undefined}
                                warnung={progWarnung}
                            />
                        </div>
                    </div>

                    {/* Rechts: Kurve — feste Hoehe */}
                    <div className="lg:col-span-7 min-w-0 h-[210px] lg:h-full">
                        {daten.verlauf ? (
                            <LeistungsVerlaufChart
                                key={`${ansicht}-${modus}`}
                                verlauf={daten.verlauf}
                                laufzeit={ansicht === 'gesamt' ? laufzeit : { ...laufzeit, abschnitte: [] }}
                                prognose={prognose}
                                modus={modus}
                            />
                        ) : (
                            <div className="h-full rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-center px-6 text-xs text-slate-400">
                                {daten.zeilen.length === 0
                                    ? 'Keine Leistungen in dieser Ansicht.'
                                    : 'Der Verlauf erscheint, sobald der Zeitraum gepflegt oder die erste Leistung gebucht ist.'}
                            </div>
                        )}
                    </div>
                </div>

                {/* Positionen — einklappbar, Vorgabe zu; der Hub merkt es sich
                    je Projekt. Zugeklappt nennt die Zeile trotzdem, was zaehlt:
                    wie viele erbracht sind und wie viele knapp werden. */}
                {alleZeilen.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={() => setPositionenOffen(!positionenOffen)}
                            aria-expanded={positionenOffen}
                            className="w-full flex items-center justify-between gap-3 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 text-left">
                                <span className="font-bold uppercase tracking-wider">Positionen</span>
                                <span className="whitespace-nowrap">{erbracht} von {mitSoll} erbracht</span>
                                {knapp > 0 && (
                                    <span
                                        className="flex items-center gap-1 text-amber-700 font-bold whitespace-nowrap"
                                        title="Soll bei gleichem Tempo vor dem Ende aufgebraucht oder schon überschritten"
                                    >
                                        <AlertTriangle className="w-3 h-3" />{knapp} × Nachtrag prüfen
                                    </span>
                                )}
                            </span>
                            <span className="flex items-center gap-1 font-bold text-slate-500 whitespace-nowrap">
                                {positionenOffen ? 'ausblenden' : `alle ${alleZeilen.length} anzeigen`}
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 motion-reduce:transition-none ${positionenOffen ? 'rotate-180' : ''}`} />
                            </span>
                        </button>
                        {positionenOffen && (
                            <PositionsListe kinder={einblenden => alleZeilen.map((z, i) => (
                                <PositionsZeile
                                    key={z.service.id}
                                    z={z}
                                    index={i}
                                    sichtbar={einblenden}
                                    gedimmt={ansicht !== 'gesamt' && z.phase !== ansicht}
                                    linie={linieFuer(z.phase)}
                                    prognose={prognosen.get(z.service.id) ?? null}
                                    mitPhase={mitPhasen}
                                    canSeeFinancials={canSeeFinancials}
                                    isReadOnly={isReadOnly}
                                    onClick={() => { if (!isReadOnly) onTrackService(z.service); }}
                                />
                            ))} />
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};
